import { registerNotificationEndpoint } from '../../services/notifications.service';
import { NOTIFICATION_PLATFORM } from '../../types/notification.types';
import type { NotificationWebPushKeys } from '../../types/notification.types';

export const WEB_PUSH_DEVICE_ID_STORAGE_KEY =
  'notifications.web_push.device_id';

export const hasWebPushVapidKey = (): boolean =>
  Boolean(import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY);

export const isWebPushSupported = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
};

const urlBase64ToUint8Array = (value: string): Uint8Array => {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary);
};

export const getWebPushDeviceId = (): string => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  const stored = window.localStorage.getItem(WEB_PUSH_DEVICE_ID_STORAGE_KEY);
  if (stored) {
    return stored;
  }

  const generated =
    typeof window.crypto.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  window.localStorage.setItem(WEB_PUSH_DEVICE_ID_STORAGE_KEY, generated);
  return generated;
};

const getSubscriptionKeys = (
  subscription: PushSubscription
): NotificationWebPushKeys | null => {
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (!p256dh || !auth) {
    return null;
  }

  return {
    p256dh: arrayBufferToBase64(p256dh),
    auth: arrayBufferToBase64(auth),
  };
};

export const WEB_PUSH_ACTIVATION_TIMEOUT_MS = 15000;

interface RegisterWebPushOptions {
  requestPermission?: boolean;
  timeoutMs?: number;
}

export type RegisterWebPushResult =
  | 'unsupported'
  | 'missing_vapid_key'
  | 'permission_default'
  | 'permission_denied'
  | 'subscription_failed'
  | 'endpoint_registration_failed'
  | 'timeout'
  | 'registered';

class WebPushActivationTimeoutError extends Error {
  constructor() {
    super('Web push activation timed out');
    this.name = 'WebPushActivationTimeoutError';
  }
}

const withTimeout = async <T>(
  promise: Promise<T>,
  timeoutMs: number
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new WebPushActivationTimeoutError());
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const withActivationDeadline = async <T>(
  promise: Promise<T>,
  deadlineAt: number
): Promise<T> => {
  const remainingMs = deadlineAt - Date.now();
  if (remainingMs <= 0) {
    throw new WebPushActivationTimeoutError();
  }

  return withTimeout(promise, remainingMs);
};

const getDeadline = (timeoutMs?: number): number =>
  Date.now() + (timeoutMs ?? WEB_PUSH_ACTIVATION_TIMEOUT_MS);

const toTimeoutOr = (
  error: unknown,
  fallback: RegisterWebPushResult
): RegisterWebPushResult =>
  error instanceof WebPushActivationTimeoutError ? 'timeout' : fallback;

const resolvePermission = async ({
  requestPermission,
  deadlineAt,
}: {
  requestPermission?: boolean;
  deadlineAt: number;
}): Promise<RegisterWebPushResult | 'granted'> => {
  let permission = Notification.permission;
  if (permission === 'default') {
    if (!requestPermission) {
      return 'permission_default';
    }

    try {
      permission = await withActivationDeadline(
        Notification.requestPermission(),
        deadlineAt
      );
    } catch (error) {
      return toTimeoutOr(error, 'permission_denied');
    }
  }

  if (permission !== 'granted') {
    return 'permission_denied';
  }

  return 'granted';
};

const resolveSubscription = async (
  vapidPublicKey: string,
  deadlineAt: number
): Promise<PushSubscription | RegisterWebPushResult> => {
  try {
    const registration = await withActivationDeadline(
      navigator.serviceWorker.register('/notifications-sw.js'),
      deadlineAt
    );
    const existingSubscription = await withActivationDeadline(
      registration.pushManager.getSubscription(),
      deadlineAt
    );
    const subscription =
      existingSubscription ??
      (await withActivationDeadline(
        registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        }),
        deadlineAt
      ));
    return subscription;
  } catch (error) {
    return toTimeoutOr(error, 'subscription_failed');
  }
};

const registerEndpoint = async (
  subscription: PushSubscription,
  deadlineAt: number
): Promise<RegisterWebPushResult> => {
  const keys = getSubscriptionKeys(subscription);
  if (!keys) {
    return 'subscription_failed';
  }

  try {
    await withActivationDeadline(
      registerNotificationEndpoint({
        platform: NOTIFICATION_PLATFORM.WEB_PUSH,
        deviceId: getWebPushDeviceId(),
        deviceName: navigator.userAgent,
        locale: navigator.language,
        webPush: {
          endpoint: subscription.endpoint,
          keys,
        },
      }),
      deadlineAt
    );
  } catch (error) {
    return toTimeoutOr(error, 'endpoint_registration_failed');
  }

  return 'registered';
};

export const registerWebPushSubscription = async (
  options: RegisterWebPushOptions = {}
): Promise<RegisterWebPushResult> => {
  if (!isWebPushSupported()) {
    return 'unsupported';
  }

  const vapidPublicKey = import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY as
    | string
    | undefined;
  if (!vapidPublicKey) {
    return 'missing_vapid_key';
  }

  const deadlineAt = getDeadline(options.timeoutMs);
  const permission = await resolvePermission({
    requestPermission: options.requestPermission,
    deadlineAt,
  });
  if (permission !== 'granted') {
    return permission;
  }

  const subscription = await resolveSubscription(vapidPublicKey, deadlineAt);
  if (typeof subscription === 'string') {
    return subscription;
  }

  return registerEndpoint(subscription, deadlineAt);
};
